
import { useCallback, useState } from "react";
import { ValidatedGuess, GuessHistory, KeyboardStatus, GameStatus, PreviousWord } from "@/lib/types/GameTypes";
import { useGuessValidator } from "./useGuessValidator";
import { useGameProgression } from "./useGameProgression";
import { toast } from "sonner";
import { timeoutManager } from "@/lib/managers/TimeoutManager";
import { trackEvent } from "@/lib/analytics";
import { trackLevelReached } from "@/lib/playerTracking";

export const useGuessSubmission = (
  currentWord: string,
  onCorrectGuess: () => void,
  onGameOver: () => void,
  onAttempt: () => void
) => {
  const {
    isValidating,
    setIsValidating,
    invalidGuessType,
    setInvalidGuessType,
    validateUserGuess
  } = useGuessValidator(currentWord);

  const {
    isTransitioning,
    setIsTransitioning,
    handleCorrectGuess,
    handleFailedAttempt
  } = useGameProgression(currentWord, onCorrectGuess, onGameOver);

  // Use memoized reference for callback to avoid unnecessary re-renders
  const submitGuess = useCallback(async (
    currentGuess: string,
    guesses: ValidatedGuess[],
    guessHistory: GuessHistory,
    levelAttempts: number[],
    gameStatus: GameStatus,
    keyboardStatus: KeyboardStatus,
    setGuesses: (guesses: ValidatedGuess[]) => void,
    setCurrentGuess: (guess: string) => void,
    setKeyboardStatus: (status: KeyboardStatus) => void,
    setGameStatus: (status: GameStatus) => void,
    setShowGameCompletion: (show: boolean) => void,
    setGuessHistory: (history: GuessHistory) => void,
    setLevelAttempts: (attempts: number[]) => void,
    setPreviousWord: (word: PreviousWord) => void
  ) => {
    timeoutManager.clearTimeout(`submit-${currentWord}`);
    
    console.log('📝 GUESS SUBMISSION START:', {
      guess: currentGuess,
      targetWord: currentWord,
      guessCount: guesses.length,
      isValidating,
      isTransitioning,
      gameStatus,
      timestamp: new Date().toISOString()
    });

    if (!currentWord || 
        isValidating || 
        isTransitioning ||
        currentGuess.length !== currentWord.length || 
        gameStatus !== 'playing') {
      console.log('📝 GUESS SUBMISSION BLOCKED:', {
        hasCurrentWord: !!currentWord,
        isValidating,
        isTransitioning,
        currentGuessLength: currentGuess.length,
        expectedLength: currentWord?.length,
        gameStatus,
        timestamp: new Date().toISOString()
      });
      return;
    }

    console.log('📝 GUESS SUBMISSION - Setting validation state', {
      isValidating: true,
      timestamp: new Date().toISOString()
    });
    
    setIsValidating(true);
    setInvalidGuessType(null);

    try {
      console.log('📝 GUESS SUBMISSION - Validating guess', {
        guess: currentGuess,
        timestamp: new Date().toISOString()
      });
      
      const validatedGuess = await validateUserGuess(currentGuess, guesses);
      
      if (!validatedGuess) {
        trackEvent('guess_invalid', {
          mode: localStorage.getItem('babblegram_game_mode') || 'daily',
          word_length: currentWord.length
        });
        console.log('📝 GUESS SUBMISSION - Validation failed, clearing current guess', {
          timestamp: new Date().toISOString()
        });
        setCurrentGuess("");
        return;
      }

      const isCorrect = validatedGuess.every(g => g.status === 'correct');
      console.log('📝 GUESS SUBMISSION - Validation result:', {
        isCorrect,
        validatedLetters: validatedGuess.map(g => ({ letter: g.letter, status: g.status })),
        timestamp: new Date().toISOString()
      });

      trackEvent('guess_submitted', {
        mode: localStorage.getItem('babblegram_game_mode') || 'daily',
        level: currentWord.length - 2,
        word_length: currentWord.length,
        attempt_number: guesses.length + 1,
        result: isCorrect ? 'correct' : 'incorrect'
      });

      const newGuesses = [...guesses, validatedGuess];
      console.log('📝 GUESS SUBMISSION - Updating UI state', {
        updatingGuesses: true,
        updatingCurrentGuess: true,
        timestamp: new Date().toISOString()
      });
      
      setGuesses(newGuesses);
      setCurrentGuess("");

      const currentLevel = currentWord.length - 2;
      const newGuessHistory = [...guessHistory];
      
      // Key by BOTH level AND word to prevent cross-word contamination
      const existingLevelHistoryIndex = newGuessHistory.findIndex(h => 
        h.level === currentLevel && h.word === currentWord
      );
      
      if (existingLevelHistoryIndex !== -1) {
        const existingGuessStrings = new Set(
          newGuessHistory[existingLevelHistoryIndex].guesses.map(
            g => JSON.stringify(g.map(letter => ({ letter: letter.letter, status: letter.status })))
          )
        );
        
        const newGuessString = JSON.stringify(
          validatedGuess.map(letter => ({ letter: letter.letter, status: letter.status }))
        );
        
        if (!existingGuessStrings.has(newGuessString)) {
          newGuessHistory[existingLevelHistoryIndex].guesses = [
            ...newGuessHistory[existingLevelHistoryIndex].guesses,
            validatedGuess
          ];
        } else {
          console.log('📝 GUESS SUBMISSION - Prevented duplicate guess in history', {
            guess: currentGuess,
            level: currentLevel,
            word: currentWord,
            timestamp: new Date().toISOString()
          });
        }
      } else {
        newGuessHistory.push({
          level: currentLevel,
          word: currentWord,
          guesses: [validatedGuess]
        });
        console.log('📝 GUESS SUBMISSION - Added new history entry', {
          level: currentLevel,
          word: currentWord,
          guessCount: 1,
          timestamp: new Date().toISOString()
        });
      }
      
      console.log('📝 GUESS SUBMISSION - Updating guess history', {
        historyEntries: newGuessHistory.length,
        timestamp: new Date().toISOString()
      });
      setGuessHistory(newGuessHistory);

      const newKeyboardStatus = { ...keyboardStatus };
      validatedGuess.forEach(({ letter, status }) => {
        const keyStatus = status === "wrong-position" ? "wrong-position" :
                          status === "correct" ? "correct" :
                          "incorrect";
                          
        if (!newKeyboardStatus[letter] || keyStatus === 'correct') {
          newKeyboardStatus[letter] = keyStatus;
        }
      });
      
      console.log('📝 GUESS SUBMISSION - Updating keyboard status', {
        timestamp: new Date().toISOString()
      });
      setKeyboardStatus(newKeyboardStatus);

      if (isCorrect) {
        console.log('📝 GUESS SUBMISSION - Correct guess! Preparing for transition', {
          word: currentWord,
          timestamp: new Date().toISOString()
        });
        
        // Create a promise that will be resolved by the timeout
        await new Promise<void>(resolve => {
          timeoutManager.setTimeout(`submit-${currentWord}`, () => resolve(), 400);
        });
        
        console.log('📝 GUESS SUBMISSION - Triggering correct guess handler', {
          currentLevel,
          timestamp: new Date().toISOString()
        });
        
        trackEvent('level_complete', {
          mode: localStorage.getItem('babblegram_game_mode') || 'daily',
          level: currentLevel,
          attempts: newGuesses.length
        });
        trackLevelReached(
          (localStorage.getItem('babblegram_game_mode') as 'daily' | 'practice' | 'custom') || 'daily',
          currentLevel
        );

        await handleCorrectGuess(
          currentLevel,
          newGuesses,
          newGuessHistory,
          levelAttempts,
          gameStatus,
          newKeyboardStatus,
          validatedGuess,
          setGuesses,
          setKeyboardStatus,
          setCurrentGuess,
          setGameStatus,
          setShowGameCompletion,
          setGuessHistory,
          setLevelAttempts,
          setPreviousWord
        );
      } else {
        console.log('📝 GUESS SUBMISSION - Incorrect guess, handling failed attempt', {
          timestamp: new Date().toISOString()
        });
        
        await handleFailedAttempt(
          currentLevel,
          newGuesses,
          setGameStatus,
          setShowGameCompletion
        );
        onAttempt();
      }
    } catch (error) {
      console.error('📝 GUESS SUBMISSION - Error:', error);
      toast.error("There was an error processing your guess. Please try again.");
    } finally {
      console.log('📝 GUESS SUBMISSION - Resetting validation state', {
        timestamp: new Date().toISOString()
      });
      setIsValidating(false);
    }
    
    console.log('📝 GUESS SUBMISSION COMPLETE', {
      timestamp: new Date().toISOString()
    });
  }, [
    currentWord,
    isValidating,
    isTransitioning,
    validateUserGuess,
    handleCorrectGuess,
    handleFailedAttempt,
    onAttempt,
    setIsValidating,
    setInvalidGuessType
  ]);

  return {
    isValidating,
    invalidGuessType,
    isTransitioning,
    submitGuess
  };
};
